import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  User as UserIcon, CreditCard, Link2, ArrowLeft, Crown,
  Loader2, Clock, Download, Trash2, AlertTriangle
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import jsPDF from 'jspdf';
import AvatarUpload from '../components/AvatarUpload';
import { ProfileAccountTab } from '../components/profile/ProfileAccountTab';
import { ProfileBillingTab } from '../components/profile/ProfileBillingTab';
import { ProfileIntegrationsTab } from '../components/profile/ProfileIntegrationsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const navigate = useNavigate();
  const { user, isPro, loading: authLoading, updateUser, refreshUser, isShopifyBilled, shopifyBilling, refreshShopifyBilling } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [receipts, setReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);

  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [loadingSub, setLoadingSub] = useState(false);
  const [subAction, setSubAction] = useState(null); // 'cancelling' | 'reactivating'
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Shopify state
  const [shopifyStatus, setShopifyStatus] = useState({ connected: false });
  const [shopifyExports, setShopifyExports] = useState([]);
  const [shopifyConnectError, setShopifyConnectError] = useState('');
  const [shopifyMessage, setShopifyMessage] = useState(null);

  // Account deletion state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Pinterest state
  const [pinterestStatus, setPinterestStatus] = useState({ connected: false, configured: false });
  const [pinterestConnecting, setPinterestConnecting] = useState(false);
  const [pinterestMessage, setPinterestMessage] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const fetchReceipts = useCallback(async () => {
    setLoadingReceipts(true);
    try {
      const res = await axios.get(`${API}/payments/receipts`);
      setReceipts(res.data.receipts || []);
    } catch (err) { console.error('Failed to fetch receipts:', err); }
    setLoadingReceipts(false);
  }, []);

  const fetchSubscription = useCallback(async () => {
    setLoadingSub(true);
    try {
      const res = await axios.get(`${API}/payments/subscription`);
      setSubscription(res.data);
    } catch (err) { console.error('Failed to fetch subscription:', err); }
    setLoadingSub(false);
  }, []);

  const fetchShopifyStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shopify/status`);
      setShopifyStatus(res.data);
    } catch (err) { console.error('Failed to fetch Shopify status:', err); }
  }, []);

  const fetchShopifyExports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shopify/exports`);
      setShopifyExports(res.data.exports || []);
    } catch (err) { console.error('Failed to fetch Shopify exports:', err); }
  }, []);

  const fetchPinterestStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pinterest/status`);
      setPinterestStatus(res.data);
    } catch (err) { console.error('Failed to fetch Pinterest status:', err); }
  }, []);

  // Handle Pinterest and Shopify OAuth callback query params
  useEffect(() => {
    const pinterestParam = searchParams.get('pinterest');
    const shopifyParam = searchParams.get('shopify');
    let changed = false;

    if (pinterestParam) {
      if (pinterestParam === 'connected') {
        const pUsername = searchParams.get('username') || '';
        setPinterestMessage({ type: 'success', text: `Pinterest account connected!` });
        fetchPinterestStatus();
      } else if (pinterestParam === 'error') {
        const reason = searchParams.get('reason') || 'unknown';
        const messages = {
          access_denied: 'You cancelled the Pinterest connection.',
          missing_params: 'Missing parameters from Pinterest.',
          invalid_state: 'Invalid connection. Please try again.',
          token_exchange_failed: 'Could not complete the connection. Please try again.',
          network_error: 'Network error. Please try again.',
        };
        setPinterestMessage({ type: 'error', text: messages[reason] || 'Unknown error during Pinterest connection.' });
      }
      searchParams.delete('pinterest');
      searchParams.delete('username');
      searchParams.delete('reason');
      setActiveTab('integrations');
      changed = true;
      setTimeout(() => setPinterestMessage(null), 8000);
    }

    if (shopifyParam) {
      if (shopifyParam === 'connected') {
        const shopName = searchParams.get('shop') || '';
        setShopifyMessage({ type: 'success', text: `Shopify store${shopName ? ` "${shopName}"` : ''} connected successfully!` });
        // Refresh status, then auto-claim if there's an unclaimed connection
        const autoClaimAndRefresh = async () => {
          try {
            const statusRes = await axios.get(`${API}/shopify/status`);
            setShopifyStatus(statusRes.data);
            // If already connected (backend linked via cookie), just refresh billing
            if (statusRes.data.connected) {
              if (refreshShopifyBilling) await refreshShopifyBilling();
            } else if (statusRes.data.pending_shop) {
              // Auto-claim the unclaimed connection
              try {
                const claimRes = await axios.post(`${API}/shopify/claim`);
                if (claimRes.data.success) {
                  const newStatus = await axios.get(`${API}/shopify/status`);
                  setShopifyStatus(newStatus.data);
                  if (refreshShopifyBilling) await refreshShopifyBilling();
                }
              } catch (claimErr) {
                console.error('Auto-claim failed:', claimErr);
              }
            }
          } catch (err) {
            console.error('Failed to refresh Shopify status:', err);
          }
          try {
            const exportsRes = await axios.get(`${API}/shopify/exports`);
            setShopifyExports(exportsRes.data.exports || []);
          } catch {}
        };
        autoClaimAndRefresh();
      } else if (shopifyParam === 'error') {
        const reason = searchParams.get('reason') || 'unknown';
        const messages = {
          missing_params: 'Missing parameters from Shopify.',
          missing_shop: 'Missing shop parameter. Please install from the Shopify App Store.',
          invalid_shop: 'Invalid shop domain received.',
          invalid_state: 'Invalid connection. Please try again.',
          hmac_failed: 'Security check failed. Please try again.',
          not_configured: 'Shopify API keys are not configured yet. Contact support.',
          token_exchange_failed: 'Could not complete the connection. Please try again.',
          no_token: 'Shopify did not provide an access token. Please try again.',
          network_error: 'Network error. Please try again.',
        };
        setShopifyMessage({ type: 'error', text: messages[reason] || 'An error occurred connecting to Shopify.' });
      }
      searchParams.delete('shopify');
      searchParams.delete('shop');
      searchParams.delete('reason');
      setActiveTab('integrations');
      changed = true;
      setTimeout(() => setShopifyMessage(null), 8000);
    }

    if (changed) {
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchPinterestStatus, fetchShopifyStatus, fetchShopifyExports, refreshShopifyBilling]);

  useEffect(() => {
    if (user) {
      if (activeTab === 'account') fetchSubscription();
      if (activeTab === 'billing') fetchReceipts();
      if (activeTab === 'integrations') {
        fetchShopifyStatus();
        fetchShopifyExports();
        fetchPinterestStatus();
      }
    }
  }, [user, activeTab, fetchReceipts, fetchSubscription, fetchShopifyStatus, fetchShopifyExports, fetchPinterestStatus]);

  // Profile edit handlers
  const startEditing = () => {
    setEditName(user?.full_name || '');
    setNameError('');
    setSaveStatus(null);
    setSaveMessage('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setNameError('');
    setSaveStatus(null);
    setSaveMessage('');
  };

  const validateName = (name) => {
    const trimmed = name.trim();
    if (!trimmed) return 'Name cannot be empty';
    if (trimmed.length < 2) return 'Name must be at least 2 characters';
    if (trimmed.length > 100) return 'Name cannot be longer than 100 characters';
    return '';
  };

  const handleSaveProfile = async () => {
    const error = validateName(editName);
    if (error) { setNameError(error); return; }
    setSaving(true);
    setSaveStatus(null);
    setSaveMessage('');
    try {
      const res = await axios.put(`${API}/auth/profile`, { full_name: editName.trim() });
      updateUser({ full_name: res.data.full_name });
      setSaveStatus('success');
      setSaveMessage('Profile updated');
      setIsEditing(false);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSaveStatus('error');
      setSaveMessage(typeof detail === 'string' ? detail : 'Could not update profile. Please try again.');
    }
    setSaving(false);
  };

  // Subscription actions
  const handleCancelSubscription = async () => {
    setSubAction('cancelling');
    try {
      await axios.post(`${API}/payments/cancel-subscription`);
      await fetchSubscription();
      if (refreshUser) await refreshUser();
      setShowCancelDialog(false);
    } catch (err) {
      console.error('Cancel subscription error:', err);
    }
    setSubAction(null);
  };

  const handleReactivateSubscription = async () => {
    setSubAction('reactivating');
    try {
      await axios.post(`${API}/payments/reactivate-subscription`);
      await fetchSubscription();
      if (refreshUser) await refreshUser();
    } catch (err) {
      console.error('Reactivate subscription error:', err);
    }
    setSubAction(null);
  };

  // Shopify — claim a pending App Store installation
  const handleShopifyClaim = async () => {
    try {
      const res = await axios.post(`${API}/shopify/claim`);
      if (res.data.success) {
        setShopifyStatus({ connected: true, shop_domain: res.data.shop_domain });
        setShopifyMessage({ type: 'success', text: 'Shopify store linked successfully.' });
        setTimeout(() => setShopifyMessage(null), 5000);
        // Refresh status
        const statusRes = await axios.get(`${API}/shopify/status`);
        setShopifyStatus(statusRes.data);
        const exportsRes = await axios.get(`${API}/shopify/exports`);
        setShopifyExports(exportsRes.data.exports || []);
        // Refresh global billing context so isShopifyBilled updates immediately
        if (refreshShopifyBilling) await refreshShopifyBilling();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not link Shopify store.';
      setShopifyConnectError(detail);
    }
  };

  const handleShopifyDisconnect = async () => {
    try {
      await axios.post(`${API}/shopify/disconnect`);
      setShopifyStatus({ connected: false });
      setShopifyExports([]);
      setShopifyMessage({ type: 'success', text: 'Shopify disconnected.' });
      if (refreshShopifyBilling) await refreshShopifyBilling();
      setTimeout(() => setShopifyMessage(null), 5000);
    } catch (err) { console.error('Shopify disconnect error:', err); }
  };

  // Account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await axios.post(`${API}/auth/delete-account`, {
        confirmation: 'DELETE',
        password: deletePassword || null,
      });
      setShowDeleteDialog(false);
      // Redirect to homepage with message
      window.location.href = '/?account_deleted=true';
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to delete account. Please try again.';
      setDeleteError(detail);
    }
    setDeleting(false);
  };

  // PDF Receipt Generation
  const generateReceiptPDF = (receipt) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = doc.internal.pageSize.getWidth();
    doc.setFillColor(196, 154, 108);
    doc.rect(0, 0, w, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text('Create by Lumera', 20, 25);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Receipt', w - 20, 25, { align: 'right' });
    doc.setTextColor(60, 60, 60);
    let y = 55;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('RECEIPT NUMBER', 20, y);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(11);
    doc.text(receipt.id || receipt.session_id || 'N/A', 20, y + 6);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('DATE', w - 20, y, { align: 'right' });
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(11);
    doc.text(receipt.created_at ? new Date(receipt.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A', w - 20, y + 6, { align: 'right' });
    y += 25;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('BILLED TO', 20, y);
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(11);
    doc.text(receipt.user_email || user?.email || '', 20, y + 6);
    y += 25;
    doc.setDrawColor(230, 230, 230);
    doc.line(20, y, w - 20, y);
    y += 10;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('DESCRIPTION', 20, y);
    doc.text('AMOUNT', w - 20, y, { align: 'right' });
    y += 8;
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    const desc = receipt.subscription_tier === 'pro' ? 'Create by Lumera Pro Plan — Monthly Subscription' : 'Create by Lumera Subscription';
    doc.text(desc, 20, y);
    doc.text(`$${(receipt.amount || 0).toFixed(2)}`, w - 20, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    y += 20;
    doc.line(20, y, w - 20, y);
    y += 10;
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('TOTAL', 20, y);
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`$${(receipt.amount || 0).toFixed(2)} USD`, w - 20, y + 2, { align: 'right' });
    y += 15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text('STATUS', 20, y);
    y += 6;
    const status = (receipt.payment_status || 'pending').toUpperCase();
    if (status === 'PAID') { doc.setTextColor(34, 197, 94); } else { doc.setTextColor(234, 179, 8); }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(status, 20, y);
    doc.setTextColor(180, 180, 180);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Thank you for your purchase. Create by Lumera — Create and sell digital products in minutes.', w / 2, 270, { align: 'center' });
    doc.save(`lumera-receipt-${receipt.id || 'receipt'}.pdf`);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const tabs = [
    { key: 'account', label: 'Account', icon: UserIcon },
    { key: 'billing', label: 'Billing', icon: CreditCard },
    { key: 'integrations', label: 'Integrations', icon: Link2 },
  ];

  // Format date to Norwegian
  const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    return new Date(isoStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '—';
    if (currency === 'nok') return `${amount} kr`;
    return `$${amount}`;
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2] dark:bg-background" data-testid="profile-page">
      {/* Nav */}
      <nav className="border-b border-[#d8cfc8]/50 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/lumera-logo.png" alt="Lumera" className="w-8 h-8 rounded-full" />
              <span className="font-serif font-semibold text-lg text-foreground">Lumera</span>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-muted-foreground font-sans text-xs h-8" data-testid="back-to-dashboard">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-serif font-bold text-stone-900 mb-1">Profile</h1>
        <p className="font-sans text-stone-500 text-sm mb-8">Manage account, billing and integrations</p>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-stone-100 rounded-full p-1 w-fit mb-8 overflow-x-auto max-w-full" data-testid="profile-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs font-sans font-semibold transition-all whitespace-nowrap ${
                activeTab === t.key ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.key === 'billing' ? 'Billing' : t.key === 'integrations' ? 'Integr.' : 'Account'}</span>
            </button>
          ))}
        </div>

        {/* ─── Account Tab ─── */}
        {activeTab === 'account' && (
          <>
          <ProfileAccountTab
            user={user} isPro={isPro} isEditing={isEditing} editName={editName}
            nameError={nameError} saving={saving} saveStatus={saveStatus} saveMessage={saveMessage}
            subscription={subscription} loadingSub={loadingSub} subAction={subAction}
            showCancelDialog={showCancelDialog}
            startEditing={startEditing} cancelEditing={cancelEditing} setEditName={setEditName}
            validateName={validateName} setNameError={setNameError} handleSaveProfile={handleSaveProfile}
            handleCancelSubscription={handleCancelSubscription}
            handleReactivateSubscription={handleReactivateSubscription}
            setShowCancelDialog={setShowCancelDialog}
            updateUser={updateUser} navigate={navigate} formatDate={formatDate} formatCurrency={formatCurrency}
          />
          {/* Delete Account */}
          <div className="mt-4 bg-white rounded-2xl border border-red-100 p-6" data-testid="delete-account-section">
            <h3 className="font-sans font-bold text-red-700 text-sm mb-2">Danger zone</h3>
            <p className="font-sans text-stone-500 text-xs mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <Button
              data-testid="delete-account-btn"
              onClick={() => { setShowDeleteDialog(true); setDeleteConfirmation(''); setDeletePassword(''); setDeleteError(''); }}
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 rounded-full px-5 h-9 text-xs font-sans font-medium"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete account
            </Button>
          </div>
          </>
        )}

        {/* ─── Billing Tab ─── */}
        {activeTab === 'billing' && (
          <ProfileBillingTab
            receipts={receipts} loadingReceipts={loadingReceipts}
            setViewingReceipt={setViewingReceipt} generateReceiptPDF={generateReceiptPDF}
            formatDate={formatDate} formatCurrency={formatCurrency}
          />
        )}

        {/* ─── Integrations Tab ─── */}
        {activeTab === 'integrations' && (
          <ProfileIntegrationsTab
            shopifyStatus={shopifyStatus} shopifyExports={shopifyExports}
            shopifyConnectError={shopifyConnectError} shopifyMessage={shopifyMessage}
            handleShopifyDisconnect={handleShopifyDisconnect}
            handleShopifyClaim={handleShopifyClaim}
            pinterestStatus={pinterestStatus} pinterestConnecting={pinterestConnecting}
            pinterestMessage={pinterestMessage}
            setPinterestStatus={setPinterestStatus} setPinterestMessage={setPinterestMessage}
            setPinterestConnecting={setPinterestConnecting}
            refreshShopifyBilling={refreshShopifyBilling}
          />
        )}
      </div>


      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={(open) => { if (!subAction) setShowCancelDialog(open); }}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl border-stone-100 p-0 overflow-hidden" data-testid="cancel-subscription-dialog">
          <div className="bg-stone-900 px-6 py-5">
            <DialogHeader>
              <DialogTitle className="text-white font-serif text-xl">Cancel subscription?</DialogTitle>
              <DialogDescription className="text-stone-400 font-sans text-sm mt-1">
                You won't lose access immediately
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3 bg-amber-50/70 border border-amber-100 rounded-xl p-4">
              <Clock className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-sans font-semibold text-stone-800 text-sm mb-1">You keep Pro access until the end of the period</p>
                <p className="font-sans text-stone-600 text-xs leading-relaxed">
                  {subscription?.current_period_end
                    ? `Pro access is active until ${formatDate(subscription.current_period_end)}. After that, your account will automatically return to the free plan.`
                    : 'You keep Pro access until the end of the current billing period.'}
                </p>
              </div>
            </div>
            <p className="font-sans text-stone-500 text-xs leading-relaxed">
              You can reactivate the subscription anytime before the period ends to avoid downgrading.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                data-testid="confirm-cancel-btn"
                onClick={handleCancelSubscription}
                disabled={!!subAction}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full py-5 font-sans font-semibold text-sm"
              >
                {subAction === 'cancelling' ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cancelling...</>
                ) : (
                  'Yes, cancel subscription'
                )}
              </Button>
              <Button
                data-testid="keep-subscription-btn"
                onClick={() => setShowCancelDialog(false)}
                disabled={!!subAction}
                variant="outline"
                className="flex-1 rounded-full py-5 font-sans font-semibold text-sm border-stone-200"
              >
                Keep Pro
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Viewer Modal */}
      <Dialog open={!!viewingReceipt} onOpenChange={() => setViewingReceipt(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="receipt-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">Receipt</DialogTitle>
            <DialogDescription className="font-sans text-xs text-stone-500">Transaction details</DialogDescription>
          </DialogHeader>
          {viewingReceipt && (
            <div className="space-y-4 mt-2">
              <div className="bg-stone-50 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-serif font-semibold text-lg text-foreground">Create by Lumera</span>
                  <span className={`text-[10px] font-sans font-bold uppercase px-2 py-0.5 rounded-full ${viewingReceipt.payment_status === 'paid' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                    {viewingReceipt.payment_status === 'paid' ? 'Paid' : viewingReceipt.payment_status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Receipt ID</p>
                    <p className="font-sans text-xs text-stone-700 font-mono">{(viewingReceipt.id || viewingReceipt.session_id || '').slice(0, 16)}</p>
                  </div>
                  <div>
                    <p className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Date</p>
                    <p className="font-sans text-xs text-stone-700">{viewingReceipt.created_at ? formatDate(viewingReceipt.created_at) : '—'}</p>
                  </div>
                  <div>
                    <p className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Billed to</p>
                    <p className="font-sans text-xs text-stone-700">{viewingReceipt.user_email}</p>
                  </div>
                  <div>
                    <p className="font-sans text-[10px] text-stone-400 uppercase tracking-wider">Plan</p>
                    <p className="font-sans text-xs text-stone-700 capitalize">{viewingReceipt.subscription_tier} Plan</p>
                  </div>
                </div>
                <div className="border-t border-stone-200 pt-3 flex items-center justify-between">
                  <span className="font-sans text-xs text-stone-500">Total</span>
                  <span className="font-sans font-bold text-xl text-stone-900">
                    {formatCurrency(viewingReceipt.amount, viewingReceipt.currency)}
                  </span>
                </div>
              </div>
              <Button data-testid="download-receipt-pdf-btn" onClick={() => generateReceiptPDF(viewingReceipt)} className="w-full bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-xl py-5 font-sans font-semibold">
                <Download className="w-4 h-4 mr-2" /> Download PDF receipt
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { if (!deleting) setShowDeleteDialog(open); }}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl border-stone-100 p-0 overflow-hidden" data-testid="delete-account-dialog">
          <div className="bg-red-600 px-6 py-5">
            <DialogHeader>
              <DialogTitle className="text-white font-serif text-xl flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Delete account
              </DialogTitle>
              <DialogDescription className="text-red-100 font-sans text-sm mt-1">
                This action is permanent and cannot be undone
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-red-50 border border-red-100 rounded-xl p-4">
              <p className="font-sans text-sm text-red-800 font-medium mb-2">What will be deleted:</p>
              <ul className="font-sans text-xs text-red-700 space-y-1.5">
                <li>Your account and all personal information</li>
                <li>All AI generation history</li>
                <li>Shopify and Pinterest connections</li>
                {isPro && <li className="font-semibold">Your active Pro subscription will be cancelled</li>}
                {isShopifyBilled && <li className="font-semibold">Your Shopify connection will be deactivated</li>}
              </ul>
            </div>

            {user?.auth_provider !== 'google' && (
              <div>
                <label className="font-sans text-xs font-semibold text-stone-600 block mb-1.5">Enter your password</label>
                <input
                  data-testid="delete-password-input"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your account password"
                  className="w-full px-3 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-red-300"
                />
              </div>
            )}

            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 block mb-1.5">
                Type <span className="font-mono text-red-600 bg-red-50 px-1 rounded">DELETE</span> to confirm
              </label>
              <input
                data-testid="delete-confirmation-input"
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type DELETE"
                className="w-full px-3 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-red-300"
              />
            </div>

            {deleteError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="delete-error">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="font-sans text-xs text-red-700">{deleteError}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                data-testid="confirm-delete-btn"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmation !== 'DELETE'}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full py-5 font-sans font-semibold text-sm disabled:opacity-50"
              >
                {deleting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Deleting...</>
                ) : (
                  'Permanently delete account'
                )}
              </Button>
              <Button
                data-testid="cancel-delete-btn"
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleting}
                variant="outline"
                className="flex-1 rounded-full py-5 font-sans font-semibold text-sm border-stone-200"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default Profile;

